import { Injectable, OnDestroy } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Observable, Subject, BehaviorSubject, of, timer, Subscription } from 'rxjs';
import { catchError, delay, retryWhen, tap, take, finalize } from 'rxjs/operators';
import { environment } from './../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AwsWebSocketService implements OnDestroy {
    private webSocket$: WebSocketSubject<any> | undefined;
    private messageSubjects: Map<string, Subject<any>> = new Map();
    private pendingSubscriptions: Map<string, boolean> = new Map();
    public status$: BehaviorSubject<string> = new BehaviorSubject<string>('CLOSED');

    // Connection management
    private isConnecting = false;
    private connectionAttempts = 0;
    private maxConnectionAttempts = 5;
    private reconnectTimeoutId: any;
    private baseReconnectDelay = 1000; // 1 second
    private maxReconnectDelay = 30000; // 30 seconds

    // Subscription tracking
    private subscriptions: Subscription[] = [];

    constructor() {
        if (!environment.useAwsWebSocket) {
            return;
        }

        // Connect on initialization after a short delay to ensure Angular is ready
        setTimeout(() => this.connect(), 1000);
    }

    ngOnDestroy(): void {
        // Clean up all subscriptions
        this.subscriptions.forEach(sub => sub.unsubscribe());

        // Clear any pending reconnect timers
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }

        // Disconnect if connected
        this.disconnect();
    }

    connect(): void {
        // Prevent multiple concurrent connection attempts
        if (this.isConnecting ||
            (this.status$.value !== 'CLOSED' &&
                this.status$.value !== 'RECONNECTING' &&
                this.status$.value !== 'ERROR')) {
            return;
        }

        // Check max reconnection attempts
        if (this.connectionAttempts >= this.maxConnectionAttempts) {
            this.status$.next('FAILED');
            // Reset connection attempts after a longer delay
            setTimeout(() => {
                this.connectionAttempts = 0;
            }, this.maxReconnectDelay);
            return;
        }

        this.isConnecting = true;
        this.connectionAttempts++;
        this.status$.next('CONNECTING');

        try {
            // Close any existing connection
            if (this.webSocket$) {
                try {
                    this.webSocket$.complete();
                } catch (e) {
                    console.error('Error closing existing WebSocket', e);
                }
            }

            // Use the WebSocket URL directly without modifications
            let wsUrl = environment.websocketApiUrl;

            // Add additional options for the WebSocket connection
            const wsOptions = {
                url: wsUrl,
                openObserver: {
                    next: () => {
                        this.status$.next('CONNECTED');
                        this.connectionAttempts = 0; // Reset counter on successful connection

                        // Wait a moment to ensure the connection is fully established
                        setTimeout(() => {
                            this.resubscribeAll();
                        }, 1000);
                    }
                },
                closeObserver: {
                    next: (event: any) => {
                        this.status$.next('CLOSED');

                        // Attempt to reconnect after delay
                        setTimeout(() => {
                            this.status$.next('RECONNECTING');
                            this.connect();
                        }, 5000);
                    }
                },
                // Add serializer and deserializer to handle messages properly
                serializer: (value: any) => {
                    try {
                        return typeof value === 'string' ? value : JSON.stringify(value);
                    } catch (e) {
                        return JSON.stringify({});
                    }
                },
                deserializer: (e: any) => {
                    try {
                        if (typeof e.data === 'string') {
                            return JSON.parse(e.data);
                        }
                        return e.data;
                    } catch (err) {
                        return {};
                    }
                },
                // Try without a specific protocol as AWS API Gateway WebSocket might not need one
                // protocol: 'mqtt'
            };

            this.webSocket$ = webSocket(wsOptions);

            const subscription = this.webSocket$.pipe(
                catchError(error => {
                    this.status$.next('ERROR');
                    this.isConnecting = false;

                    // Calculate exponential backoff delay
                    const reconnectDelay = Math.min(
                        this.baseReconnectDelay * Math.pow(2, this.connectionAttempts - 1),
                        this.maxReconnectDelay
                    );

                    console.log(`WebSocket reconnecting in ${reconnectDelay/1000} seconds (attempt ${this.connectionAttempts})`);

                    // Clear any existing timeout
                    if (this.reconnectTimeoutId) {
                        clearTimeout(this.reconnectTimeoutId);
                    }

                    // Schedule reconnection with exponential backoff
                    this.reconnectTimeoutId = setTimeout(() => {
                        this.status$.next('RECONNECTING');
                        this.connect();
                    }, reconnectDelay);

                    throw error;
                }),
                finalize(() => {
                    this.isConnecting = false;
                })
            ).subscribe({
                next: (message) => {
                    this.handleMessage(message);
                },
                error: (error) => {
                    console.error('WebSocket error:', error);
                    this.status$.next('ERROR');
                    this.isConnecting = false;

                    // Calculate exponential backoff delay
                    const reconnectDelay = Math.min(
                        this.baseReconnectDelay * Math.pow(2, this.connectionAttempts - 1),
                        this.maxReconnectDelay
                    );

                    console.log(`WebSocket reconnecting in ${reconnectDelay/1000} seconds (attempt ${this.connectionAttempts})`);

                    // Clear any existing timeout
                    if (this.reconnectTimeoutId) {
                        clearTimeout(this.reconnectTimeoutId);
                    }

                    // Schedule reconnection with exponential backoff
                    this.reconnectTimeoutId = setTimeout(() => {
                        this.status$.next('RECONNECTING');
                        this.connect();
                    }, reconnectDelay);
                },
                complete: () => {
                    this.isConnecting = false;
                    if (this.status$.value !== 'CLOSED') {
                        this.status$.next('CLOSED');
                    }
                }
            });

            // Track subscription for cleanup
            this.subscriptions.push(subscription);
        } catch (error) {
            this.status$.next('ERROR');

            // Calculate exponential backoff delay
            const reconnectDelay = Math.min(
                this.baseReconnectDelay * Math.pow(2, this.connectionAttempts - 1),
                this.maxReconnectDelay
            );

            console.log(`WebSocket reconnecting in ${reconnectDelay/1000} seconds (attempt ${this.connectionAttempts})`);

            // Clear any existing timeout
            if (this.reconnectTimeoutId) {
                clearTimeout(this.reconnectTimeoutId);
            }

            // Schedule reconnection with exponential backoff
            this.reconnectTimeoutId = setTimeout(() => {
                this.status$.next('RECONNECTING');
                this.connect();
            }, reconnectDelay);
        }
    }

    private resubscribeAll(): void {
        // Resubscribe to all destinations
        this.messageSubjects.forEach((_, destination) => {
            this.sendSubscribeMessage(destination);
        });

        // Also try to subscribe to any pending subscriptions
        this.pendingSubscriptions.forEach((_, destination) => {
            this.sendSubscribeMessage(destination);
        });
    }

    private sendSubscribeMessage(destination: string): void {
        if (!this.webSocket$) {
            this.pendingSubscriptions.set(destination, true);
            return;
        }

        if (this.status$.value !== 'CONNECTED') {
            this.pendingSubscriptions.set(destination, true);
            return;
        }

        try {
            // Try different message formats for AWS API Gateway WebSocket
            // Format 1: Standard format used previously
            const subscribeMessage1 = {
                action: 'subscribe',
                destination: destination
            };

            // Format 2: AWS API Gateway WebSocket format with 'action' as the route key
            const subscribeMessage2 = {
                action: 'subscribe',
                data: {
                    destination: destination
                }
            };

            // Use the first format for now
            const subscribeMessage = subscribeMessage1;

            this.webSocket$.next(subscribeMessage);

            // Remove from pending if it was there
            this.pendingSubscriptions.delete(destination);
        } catch (error) {
            this.pendingSubscriptions.set(destination, true);
        }
    }

    subscribe(destination: string): Observable<any> {
        // Create a new subject for this destination if it doesn't exist
        if (!this.messageSubjects.has(destination)) {
            console.log(`Creating new subscription for ${destination}`);
            this.messageSubjects.set(destination, new Subject<any>());

            // Send the subscribe message to AWS if connected
            if (this.status$.value === 'CONNECTED' && this.webSocket$) {
                this.sendSubscribeMessage(destination);
            } else {
                // Mark as pending subscription to be processed when connected
                console.log(`Queueing subscription to ${destination} for when connection is established`);
                this.pendingSubscriptions.set(destination, true);

                // If not connecting or connected, initiate connection
                if (this.status$.value === 'CLOSED' || this.status$.value === 'ERROR') {
                    this.connect();
                }
            }
        } else {
            console.log(`Reusing existing subscription for ${destination}`);
        }

        return this.messageSubjects.get(destination)!.asObservable();
    }

    send(destination: string, payload: any): void {
        if (!this.webSocket$) {
            console.log('No WebSocket connection, connecting before sending message');
            this.connect();

            // Create a subscription to wait for connection and then send
            const sub = this.status$.subscribe(status => {
                if (status === 'CONNECTED') {
                    setTimeout(() => this.send(destination, payload), 500);
                    sub.unsubscribe();
                } else if (status === 'FAILED') {
                    console.error('Failed to establish WebSocket connection for sending message');
                    sub.unsubscribe();
                }
            });

            // Add to cleanup subscriptions
            this.subscriptions.push(sub);
            return;
        }

        if (this.status$.value !== 'CONNECTED') {
            console.log('WebSocket not connected, waiting for connection before sending');
            // Try to connect and then retry when connected
            const sub = this.status$.subscribe(status => {
                if (status === 'CONNECTED') {
                    setTimeout(() => this.send(destination, payload), 500);
                    sub.unsubscribe();
                } else if (status === 'FAILED') {
                    console.error('Failed to establish WebSocket connection for sending message');
                    sub.unsubscribe();
                }
            });

            // Add to cleanup subscriptions
            this.subscriptions.push(sub);
            return;
        }

        try {
            // Try different message formats for AWS API Gateway WebSocket
            // Format 1: Standard format used previously
            const sendMessage1 = {
                action: 'send',
                message: {
                    destination,
                    payload,
                    headers: {}
                }
            };

            // Format 2: AWS API Gateway WebSocket format with 'action' as the route key
            const sendMessage2 = {
                action: 'send',
                data: {
                    destination,
                    payload
                }
            };

            // Use the first format for now
            const sendMessage = sendMessage1;

            this.webSocket$.next(sendMessage);
        } catch (error) {
            // Error sending message
        }
    }

    disconnect(): void {
        if (this.status$.value === 'CONNECTED' || this.status$.value === 'CONNECTING') {
            // Mark as disconnecting
            this.status$.next('DISCONNECTING');

            // Clear all subscriptions
            this.messageSubjects.forEach(subject => subject.complete());
            this.messageSubjects.clear();
            this.pendingSubscriptions.clear();

            // Clear any reconnection timeouts
            if (this.reconnectTimeoutId) {
                clearTimeout(this.reconnectTimeoutId);
                this.reconnectTimeoutId = null;
            }

            // Reset connection attempts
            this.connectionAttempts = 0;

            // Close connection
            if (this.webSocket$) {
                try {
                    this.webSocket$.complete();
                } catch (e) {
                    console.error('Error closing WebSocket connection', e);
                }
            }

            this.isConnecting = false;
            this.status$.next('CLOSED');
        }
    }

    private handleMessage(message: any): void {
        try {
            let destination: string;
            let payload: any;

            if (message.destination) {
                destination = message.destination;
                payload = message.payload;
            } else if (message.body) {
                // Handle STOMP-like format
                const data = typeof message.body === 'string' ? JSON.parse(message.body) : message.body;
                destination = message.headers ? message.headers.destination : data.destination;
                payload = data.payload || data;
            } else {
                return;
            }

            const subject = this.messageSubjects.get(destination);
            if (subject) {
                subject.next(payload);
            } else {
                // Auto-create subject for this destination if it doesn't exist yet
                const newSubject = new Subject<any>();
                this.messageSubjects.set(destination, newSubject);
                newSubject.next(payload);
            }
        } catch (error) {
            // Error handling message
        }
    }

    // Method to force a subscription
    forceSubscribe(destination: string): void {
        this.sendSubscribeMessage(destination);
    }
}
